import React, { useEffect, useRef, useState } from 'react';
import { Checkbox, Input } from '@digital-pulse/react';
import { DOB_LABELS } from '@/lib/constants/dateOfBirth';
import { onlyNumberKey } from '@/lib/utils/utils';
import styles from './dateFieldsetWrapper.module.scss';

interface DateFieldsetWrapperProps {
  required?: boolean;
  isErrorAnnounced?: boolean;
  refProp?: React.Ref<any>;
  legend?: string;
  helper?: string;
  error?: string;
  dataTestId?: string;
  register: any;
  hasShowHide?: boolean;
  // only applies when hasShowHide is true; defaults to masked
  isShowHideChecked?: boolean;
  showHideLabel?: string;
  onShowHideClick?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const DateFieldsetWrapper = ({
  required = true,
  isErrorAnnounced = true,
  refProp,
  legend = DOB_LABELS.DATE_OF_BIRTH,
  helper = DOB_LABELS.DATE_OF_BIRTH_HELPER,
  error,
  dataTestId,
  register,
  hasShowHide = false,
  isShowHideChecked = false,
  showHideLabel,
  onShowHideClick
}: DateFieldsetWrapperProps) => {
  // Pulse's Input has no isShowHideChecked prop and its built-in toggle always starts
  // unchecked, so own the masking state here and render the checkbox ourselves.
  const [isDobShown, setIsDobShown] = useState(isShowHideChecked);
  const hasUserToggled = useRef(false);

  // router.query is empty until Next's router is ready, so isShowHideChecked can arrive
  // late — re-seed until the user takes over, then leave their choice alone.
  useEffect(() => {
    if (!hasUserToggled.current) {
      setIsDobShown(isShowHideChecked);
    }
  }, [isShowHideChecked]);

  const handleShowHideChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    hasUserToggled.current = true;
    setIsDobShown(e.target.checked);
    onShowHideClick?.(e);
  };

  return (
    <div className={styles.dobFieldGroup}>
      <Input
        ref={refProp}
        id={dataTestId}
        type={hasShowHide && !isDobShown ? 'password' : 'text'}
        label={legend}
        helper={helper}
        error={error}
        data-test-id={dataTestId}
        required={required}
        isErrorAnnounced={isErrorAnnounced}
        inputMode="numeric"
        onKeyDown={(e) => onlyNumberKey(e)}
        onInput={(e) => {
          const target = e.target as HTMLInputElement;
          target.value = target.value.replace(/[^\d]/g, '').slice(0, 8);
        }}
        {...register('dateOfBirth')}
      />
      {hasShowHide && (
        <Checkbox
          id={`${dataTestId}-show-hide`}
          data-test-id={`${dataTestId}-show-hide`}
          aria-controls={dataTestId}
          checked={isDobShown}
          label={showHideLabel || DOB_LABELS.SHOW_DATE_OF_BIRTH}
          onChange={handleShowHideChange}
        />
      )}
    </div>
  );
};

export default DateFieldsetWrapper;
